import * as React from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import { media } from "src/library/utils";

import Grid from "../Hedron";
import { Button } from "../Button";
import { ImageCropper } from "../Image";
import { BasicModal } from "./BasicModal";

interface ImageCroppingModalProps {
  show: boolean;
  src: File;
  onDismiss: () => void;
  onSubmit: (file: File) => void;
  title?: string;
  cropInsetPercentage?: number;
  aspect?: number;
}

/**
 * Image cropping modal
 */
export const ImageCroppingModal: React.ReactFCWithChildren<ImageCroppingModalProps> = ({
  title = "Crop image",
  cropInsetPercentage = 1, // 100%
  aspect = 3 / 2, // 3:2 aspect ratio equivalent
  show,
  src,
  onDismiss,
  onSubmit
}) => {
  const [ blob, setBlob ] = React.useState<Blob>();

  /**
   * Handles the modals "Done" click functionality
   *
   * Converts the cropped image Blob to a File before passing it back to the origin
   * via the given onSubmit function
   */
  const handleDoneClick = React.useCallback(() => {
    if (!blob) {
      return;
    }

    // Convert the Blob to a File and call the given onSubmit function to
    // pass the cropped image File back to the caller
    onSubmit(new File([ blob ], `${moment.utc().unix()}_${src.name}`, { type: src.type }));
  }, [
    src,
    blob,
    onSubmit
  ]);

  return (
    <BasicModal
      title={title}
      open={show}
      onDismiss={onDismiss}
    >
      <ImageCropper
        src={src}
        aspect={aspect}
        cropInsetPercentage={cropInsetPercentage}
        onComplete={blob => {
          setBlob(blob);
        }}
      />

      <StyledButtonWrapper>
        <Button
          $buttonType="secondary"
          onClick={onDismiss}
        >
          Cancel
        </Button>

        <Button
          $buttonType="primary"
          onClick={handleDoneClick}
        >
          Done
        </Button>
      </StyledButtonWrapper>
    </BasicModal>
  );
};

// const StyledLoadingSpinnerWrapper = styled.div`
//   position: relative;
//   padding: 4rem 2rem;
// `;

const StyledButtonWrapper = styled(Grid.Box)`
  ${({ theme, spacing }) => css`
    display: flex;
    flex-direction: row;
    justify-content: ${spacing || "space-between"};

    margin-top: ${theme.spacing.margins.sm};
    padding: ${theme.spacing.padding.md} ${theme.spacing.padding.lg};
    border-top: 1px solid ${theme.colors.lightestGrey};

    ${media({ lt: theme.breakpoints.sm })} {
      flex-direction: column;

      > button {
        width: 100%;
        margin-bottom: ${theme.spacing.margins.sm};
      }
    }
  `}
`;
